import { checkEmailOrPhone, emailRegExp, getParsedContact } from "constant/messageConstants";
import FloatLabel from "components/Authentication/Common/FloatLabel/FloatLabel";
import SocialLogins from "components/Authentication/Common/SocialLogins";
import React, { useState, useEffect, useRef } from "react";
import AppButton from "components/AppButton/AppButton";
import FormInput from "components/FormItems/FormInput";
import { LoadingOutlined } from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";
import useWindowWidth from "hooks/useWindowWidth";
import { inject, observer } from "mobx-react";
import useRecaptcha from "hooks/useRecaptcha";
import { Col, Row, Typography } from "antd";
import { Form, Formik } from "formik";
import * as Yup from "yup";

const SignInForm = props => {
  const { authStore } = props;
  const inputRef = useRef();
  const formikRef = useRef();
  const history = useHistory();
  const { width } = useWindowWidth();
  const [isNumber, setIsNumber] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const contactInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const btnWidth = width < 768 ? "23.2rem" : "31rem";

  const { RecaptchaComponent, executeRecaptcha, resetRecaptcha } = useRecaptcha();

  useEffect(() => {
    if (showPasswordField && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
    inputRef?.current?.focus({
      cursor: "end"
    });
  }, [showPasswordField]);

  useEffect(() => {
    contactInputRef.current?.focus?.({ cursor: "end" });
  }, []);

  const initialValues = {
    contact: localStorage.getItem("contact") || "",
    password: ""
  };

  const LoginSchema = Yup.object().shape({
    contact: Yup.string()
      .matches(emailRegExp, "Please enter a valid email")
      .required("Enter Email"),
    password: Yup.string().when([], {
      is: () => showPasswordField,
      then: Yup.string()
        .required("Password is required")
        .min(6, "Minimum 6 is required!")
        .max(20, "Maximum 20 allowed")
        .matches(/^\S*$/, "Password must not contain spaces")
    })
  });

  const handlePasswordSubmit = async (data, setSubmitting, captchaToken) => {
    try {
      const payload = { ...data, captchaToken };
      const res = await authStore.loginUser(payload);
      if (res.response.status) {
        if (!res?.response?.data?.mfaEmailList) {
          if (res.response.data.message && res.response.data.message.includes("number")) {
            localStorage.setItem("mobile", res.response.data.mobile);
            localStorage.removeItem("contact");
          } else if (res.response.data.message && res.response.data.message.includes("email")) {
            localStorage.setItem("contact", res.response.data.email);
            localStorage.removeItem("mobile");
          }

          if (res.response?.data && !res.response.data.active && !res.response.data.isMobileVerified && !res.response.data.isEmailVerified) {
            history.push("/signup-mobile");
          } else {
            history.push("/verification");
          }
        } else {
          history.push("/mfa-email-list");
        }
      } else {
        console.log(res.message);
      }
    } catch (error) {
      console.error("Login Error:", error);
    } finally {
      resetRecaptcha();
      setSubmitting(false);
    }
  };

  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  return (
    <Row type="flex">
      <Col span={24} className="text-start">
        <Typography.Title level={3} className="mb-3">
          Log In
        </Typography.Title>
      </Col>
      <Col span={24} className="text-start  mb-2 ">
        <Typography.Text className={`text-dark fw-bold`}>
          <span>
            New to Guni?{" "}
            <span
              role="button"
              className="text-info-auth"
              style={{ textDecoration: "underline", cursor: "pointer" }}
              onMouseEnter={e => (e.target.style.textDecoration = "none")}
              onMouseLeave={e => (e.target.style.textDecoration = "underline")}
              onClick={() => history.push("/signup")}
            >
              Create Account
            </span>
          </span>
        </Typography.Text>
      </Col>

      {RecaptchaComponent}

      <Col span={24}>
        <Formik
          initialValues={initialValues}
          validationSchema={LoginSchema}
          innerRef={formikRef}
          onSubmit={async (values, { setSubmitting }) => {
            const captchaToken = await executeRecaptcha();
            const formatedVal = getParsedContact(values.contact);
            localStorage.setItem("contact", formatedVal);
            const data = {
              captchaToken,
              ...checkEmailOrPhone(formatedVal)
            };

            if (showPasswordField) {
              data.password = values.password;
              handlePasswordSubmit(data, setSubmitting);
            } else {
              authStore
                .getUserByContact(data)
                .then(res => {
                  setSubmitting(false);
                  if (res.status) {
                    if (res.data.otp) {
                      if (res.data.message.includes("email")) {
                        localStorage.setItem("contact", res.data.email);
                      } else {
                        localStorage.setItem("mobile", res.data.mobile);
                      }
                      history.push("/verification");
                    } else {
                      // history.push("/login");
                      setShowPasswordField(true);
                    }
                  } else {
                    console.log(res?.message.includes("not Found"), res?.message);
                    // if account is not active then stop redirecting
                    if (res?.message.includes("not Found")) history.push("/signup");
                  }
                })
                .catch(err => {
                  setSubmitting(false);
                  console.log(err);
                });
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col span={24}>
                    <FormInput
                      label={<h6 className="font-weight-bold">Email</h6>}
                      placeholder={null}
                      containerProps={{
                        colon: false
                      }}
                      value={values.contact}
                      name="contact"
                      type="text"
                      containerClassname={`mb-2 `}
                      inputClassname="border-dark text-muted fs-6 fw-bold form-input-fixed-width"
                      onChange={e => {
                        handleChange(e);
                        setIsNumber(!isNaN(e.target.value));
                      }}
                      inputProps={{
                        ref: contactInputRef
                      }}
                      error={errors.contact}
                      touched={touched.contact}
                    />
                  </Col>
                  {showPasswordField && (
                    <Col span={24}>
                      <FormInput
                        label={<h6 className="font-weight-bold">Password</h6>}
                        placeholder={null}
                        containerProps={{
                          colon: false
                        }}
                        value={values.password}
                        name="password"
                        type={passwordShown ? "text" : "password"}
                        suffix={
                          <span onClick={togglePassword} className="text-info-auth" style={{ cursor: "pointer" }}>
                            {passwordShown ? "Hide" : "Show"}
                          </span>
                        }
                        inputClassname="border-dark text-muted fs-6 fw-bold form-input-fixed-width"
                        containerClassname="mb-2 border-dark"
                        onChange={handleChange}
                        error={errors.password}
                        touched={touched.password}
                        inputProps={{
                          ref: passwordInputRef,
                          style: { width: "31rem" }
                        }}
                      />
                    </Col>
                  )}

                  {errors.submit && (
                    <Col span={24} className="text-center mb-2">
                      <Typography.Text type="danger">{errors.submit}</Typography.Text>
                    </Col>
                  )}

                  <Col span={24} className="text-start">
                    <AppButton
                      label={isSubmitting ? <LoadingOutlined /> : "Continue"}
                      className={` mb-2 px-4 py-2 rounded mt-2`}
                      type="submit"
                      authBtn
                      disabled={isSubmitting}
                      style={{ width: btnWidth }}
                    />
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Col>

      <Col span={24} className="text-center pb-5 ">
        <Link to="/password-recovery" className="text-info-auth">
          Forgot Password?
        </Link>
      </Col>
      <Col span={24}>
        <SocialLogins text="Sign in with" />
      </Col>
    </Row>
  );
};

export default inject(stores => ({
  authStore: stores.store.authStore
}))(observer(SignInForm));
